/**
 * ManualLookup — the manual/dead-phone path: production port of the
 * confirmed studio proof
 * (the design studio's features/scanner-lookup/scanner-lookup.view.ts,
 * committed c64f6e9/0737714/5843b68), wired to real state. Opened from
 * scan.view.ts's own "Can't scan? Get help" affordance as a nested `show()`
 * overlay (mirroring settings.view.ts's own pattern in this package).
 *
 * THE MODEL, carried forward verbatim: scroll is the front door — an empty
 * query shows every guest, German-alphabetised into letter groups with
 * sticky headers and an A–Z jump rail (the drag-scrub gesture and its
 * geometry are described below — schmancy has no alphabet-index primitive, a
 * recorded library gap, so this is the one hand-rolled element). Typing
 * filters that SAME list through manual-lookup.state.ts's German-aware
 * `similarity()` wrapper. A browse/search row answers "which one is this
 * person" — name, email, ticket count, how long ago they bought
 * (`purchaseAgo`, below); no chevron (`schmancy-list-item` already carries
 * hover/press physics), no per-row ticket dump, no check-in action on the
 * row. All four render in both browse and search alike: name and email
 * identify the guest, ticket count and recency help confirm the match is
 * right. Tapping a row is a second beat: it opens `<order-detail>`
 * (@momo/views/orders) — the same order overlay the admin Orders tab opens
 * on its own order rows, one component, both contexts — where the buyer
 * header, the ticket cards, the order summary, and the per-ticket check-in
 * action live, honestly reduced to what this screen offers: `showFixEmail`,
 * `showInvoice`, `showRefund` and `showPurge` are all false (this domain's own
 * order-detail.sync.ts header explains why), so Fix-email, the invoice
 * section, the refund step and the purge never render here regardless of the
 * signed-in actor's own permissions. No party
 * blanket-check-in. Two permission states: `tickets.checkin.scan` alone gets
 * the escalate card, no search box at all (a privacy decision — guest
 * details stay off every door phone); only `+ tickets.reports.view` gets
 * this screen's real job. That gate is `hasReportsPermission`
 * (checkin.state.ts), mirroring `Order`'s own `list` rule exactly.
 *
 * The A–Z rail spans the browse column's own full height, one equal-height
 * slot per letter actually present, so a drag's vertical travel maps onto
 * the complete letter range regardless of how many groups exist — never a
 * short stub floating mid-edge with dead space above and below it. A scrub
 * position resolves by proportional index math against the rail's own
 * bounding rect — `fraction = clamp((y - rail.top) / rail.height, 0, 1)`,
 * indexed straight into `rail.children` — not a pixel-exact hit test, so
 * every pixel of the rail's height lands on exactly one letter with no gap
 * it can stall in. The rail itself carries a fixed `w-11` (44px) rather than
 * shrinking to its glyph content's own width — a real touch-target minimum
 * (iOS HIG's own 44pt) — and `@pointerdown` lives on the rail `div` itself,
 * not the individual letter buttons, so that entire 44px-wide padding box is
 * live hit surface the instant a pointer lands anywhere inside it; the glyph
 * stays small and centered for legibility while the strip a thumb actually
 * has to land on is the full pill width. A haptic tick (`navigator.vibrate`,
 * feature-detected) and a filled highlight on the live letter's own button
 * fire on every letter change, alongside the floating magnified bubble
 * beside the thumb. A single tap still works (and is what keyboard/screen-
 * reader activation uses, via each button's own `@click`) — the drag is an
 * additive gesture layered on top of the tap, not a replacement.
 *
 * Chrome stays collapsed: no title, no subtitle for either permission state
 * — the operator arrived here by tapping the scan screen's own "Can't scan?
 * Get help" affordance, so a headline restating that tap, or a subtitle
 * explaining what the search box does, is a second copy of what the box's
 * own placeholder already says. The search input rides the header itself,
 * beside the back button, one row — no separate label above it, only a
 * placeholder ("Search by name or email") plus an explicit `aria-label`
 * (a placeholder is not an accessible name). The escalate path keeps only
 * the back button: its own body already carries the full explanation.
 *
 * The host declares its own footprint — 64rem wide, 90svh tall, each
 * clamped to `100%` of the box the overlay left it. `show()` names no
 * layout and no size: an anchored surface is `w-max` under a viewport cap,
 * so a mount that asks for nothing renders at its own max-content — a
 * two-guest result would be a narrow pill, and every keystroke that
 * narrows the list would resize the panel under the operator's thumb. The
 * `100%` clamps are what let that viewport cap reach through, since the
 * overlay's mount is a `minmax(0, 1fr)` grid track and a percentage
 * resolves against it. The definite height is also what every `h-full`
 * below here, several grid levels deep, resolves against — a custom
 * element defaults to `display: inline` with no intrinsic height, and
 * without both the browse list could not scroll at all; each intermediate
 * grid row that hosts the flexible `1fr` track also carries `min-h-0`,
 * since a grid item's automatic minimum size is its content's min-content
 * size unless told otherwise. `jumpTo` passes `behavior: "instant"`
 * explicitly — `overflowWithin()` sets `scroll-behavior: smooth` on its own
 * element, which made a rail tap or the drag-scrub's rapid successive calls
 * land at a non-deterministic scroll position.
 *
 * The ticket-type filter chips ride inside a `<schmancy-scroll
 * direction="horizontal" hide>`, never `wrap`: `<schmancy-chips>`'s own host
 * is `width: fit-content`, and a bare `w-full` class does not reliably
 * out-cascade that, so an unwrapped row can grow past its own container and
 * overlap whatever sits beside it. `schmancy-scroll` is the actual
 * bounding/scrolling container instead, left at its natural width, free to
 * overflow honestly inside it.
 *
 * `lookupOrders` below is this file's own module-scope join of
 * `ordersIndex` (manual-lookup.state.ts) against the scan path's already-
 * live `ticketsIndex` (tickets.state.ts) — a `computed` reading two state
 * modules from a `.view.ts` file's module scope, rather than living inside
 * `manual-lookup.state.ts`, whose own import allow-list excludes a sibling
 * `.state.ts` file (see that file's header for the one shape that does
 * cross that seam, which this join is not). The type-filter chip row reuses
 * `acceptedTypeIds` (settings.state.ts) — this door's own real, persistent,
 * event-gated accepted-types config — rather than the studio's necessarily-
 * self-contained transient stand-in for it (that file's own header explains
 * why it couldn't import the real one). `originFilter` (settings.state.ts)
 * narrows `lookupOrders` the same way, ahead of both the type-chip pool and
 * the browse/search list — a guest-only door's manual path must not surface
 * a sold guest of the same type the scanner itself would refuse.
 *
 * `lookupScreen`'s own open/close is this component's mount lifecycle:
 * manual-lookup.sync.ts's own listener opens only while this view is
 * connected (see that file's header for why — an ordinary door never pays
 * this read, and even a reports.view holder pays it only while the screen
 * is actually open).
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { computed } from "@mhmo91/schmancy/state";
import { type Locale, translate } from "@momo/i18n";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { hasReportsPermission } from "../checkin.state.ts";
import { acceptedTypeIds, originFilter } from "../settings/settings.state.ts";
import { ticketTypeName } from "../ticket-types/ticket-types.state.ts";
import "../../events/console/order-detail.view.ts";
import { css, html } from "lit";
import { state } from "lit/decorators.js";
import { guard } from "lit/directives/guard.js";
import { createRef, type Ref, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { fromEvent, merge, Subject, takeUntil } from "rxjs";
import {
	distinctUntilChanged,
	endWith,
	filter,
	map,
	startWith,
	switchMap,
	tap,
} from "rxjs/operators";
import { answeredTickets } from "../tickets/tickets.state.ts";
import {
	answeredOrders,
	browseGroups,
	filterByOrigin,
	filterByTicketTypes,
	type LookupOrder,
	type LookupTicket,
	lookupScreen,
	rankOrders,
	ticketTypeOptions,
} from "./manual-lookup.state.ts";

/** The joined browse/search source — every locally-indexed order paired
 *  with its own tickets, resolved from the scan path's live ticket index.
 *  See this file's own header for why the join lives here, not in
 *  manual-lookup.state.ts. */
const lookupOrders = computed((): readonly LookupOrder[] => {
	const tickets = answeredTickets.get();
	return [...(answeredOrders.get()?.byOrderId.values() ?? [])].map((order) => {
		const ticketIds = tickets?.byOrderId.get(order.orderId) ?? [];
		return {
			...order,
			tickets: ticketIds
				.map((id) => tickets?.byTicketId.get(id))
				.filter((t): t is NonNullable<typeof t> => t !== undefined)
				.map(
					(t): LookupTicket => ({
						ticketId: t.ticketId,
						typeId: t.typeId,
						status: t.status,
					}),
				),
		};
	});
});

/** Whether the indexes this screen states names and counts from have
 *  answered: the ticket index for every door, and the order index too where
 *  this session sees names. */
const lookupAnswered = computed(
	() =>
		answeredTickets.get() !== null &&
		(!hasReportsPermission.get() || answeredOrders.get() !== null),
);

/** A guest volunteers this unprompted ("I bought it a few days ago"), so it
 *  helps FIND them while browsing too, not only while disambiguating a
 *  search result. Mirrors
 *  `splash.view.ts`'s own `formatAgo` shape (a locale-parameterised, single-
 *  caller export — `.view.ts`, not `.state.ts`, since the latter's own
 *  import allow-list excludes `@momo/i18n`'s `Locale`), scaled for a
 *  purchase's own day-to-months span rather than a scan's seconds-to-hours
 *  one. */
/** Built once per locale, not once per row: a browse list is every guest of
 *  the event and an ICU formatter is expensive to construct. */
const RELATIVE_TIME = new Map<Locale, Intl.RelativeTimeFormat>();

export function purchaseAgo(date: Date, locale: Locale): string {
	const days = Math.round(
		(Date.now() - date.getTime()) / (24 * 60 * 60 * 1000),
	);
	let rtf = RELATIVE_TIME.get(locale);
	if (!rtf) {
		rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
		RELATIVE_TIME.set(locale, rtf);
	}
	if (days < 7) return rtf.format(-days, "day");
	const weeks = Math.round(days / 7);
	if (weeks < 5) return rtf.format(-weeks, "week");
	const months = Math.round(days / 30);
	if (months < 12) return rtf.format(-months, "month");
	return rtf.format(-Math.round(months / 12), "year");
}

@schmancyElement("manual-lookup-view")
export default class ManualLookupView extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
      :host {
        display: block;
        width: 64rem;
        max-width: 100%;
        height: 90svh;
        max-height: 100%;
      }
    `,
	];

	@state() private query = "";
	@state() private selectedTypeIds = new Set<string>();
	@state() private scrubbing = false;
	@state() private scrubLetter = "";
	private sectionRefs = new Map<string, Ref<HTMLDivElement>>();
	private readonly railPointerDown$ = new Subject<PointerEvent>();

	override connectedCallback() {
		super.connectedCallback();
		lookupScreen.set({ open: true });

		this.railPointerDown$
			.pipe(
				switchMap((down) => {
					const rail = down.currentTarget as HTMLElement;
					return fromEvent<PointerEvent>(rail, "pointermove").pipe(
						// The initial press itself resolves a letter — a plain tap
						// (no drag at all) still jumps, so the gesture never demands
						// a drag to do anything at all.
						startWith(down),
						// Proportional index math against the rail's own rect, not a
						// pixel-exact hit test: every pixel of the rail's height maps to
						// exactly one letter, with no gap between buttons the gesture can
						// stall in, and rail.children reads the letter straight off the
						// button actually rendered for that slot — live at gesture time,
						// so it stays correct as the chip filter changes which groups
						// exist between mounts.
						map((ev) => {
							const rect = rail.getBoundingClientRect();
							const fraction = Math.min(
								Math.max((ev.clientY - rect.top) / rect.height, 0),
								1,
							);
							const index = Math.min(
								rail.children.length - 1,
								Math.floor(fraction * rail.children.length),
							);
							return {
								letter: (rail.children[index] as HTMLElement | undefined)
									?.dataset.letter,
								clientY: ev.clientY,
								railRect: rect,
							};
						}),
						filter(
							(
								r,
							): r is { letter: string; clientY: number; railRect: DOMRect } =>
								!!r.letter,
						),
						tap(({ clientY, railRect }) => {
							const hostRect = this.getBoundingClientRect();
							this.style.setProperty(
								"--bubble-x",
								`${railRect.left - hostRect.left}px`,
							);
							this.style.setProperty(
								"--bubble-y",
								`${clientY - hostRect.top}px`,
							);
						}),
						distinctUntilChanged((a, b) => a.letter === b.letter),
						tap(({ letter }) => {
							this.scrubLetter = letter;
							this.jumpTo(letter);
							// One tick per letter crossed, not per pointermove — a fast
							// flick that skips several letters in one browser-coalesced
							// event still ticks once, confirming the live letter changed
							// rather than counting every letter passed.
							navigator.vibrate?.(10);
						}),
						map(() => true as const),
						takeUntil(
							merge(
								fromEvent(rail, "pointerup"),
								fromEvent(rail, "pointercancel"),
							),
						),
						endWith(false as const),
					);
				}),
				tap((active) => {
					this.scrubbing = active;
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe();
	}

	override disconnectedCallback() {
		super.disconnectedCallback();
		lookupScreen.set({ open: false });
	}

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	override render() {
		const reports = hasReportsPermission.get();
		return html`
      <div
        class="relative grid h-full min-h-0 w-full select-none touch-manipulation grid-rows-[auto_1fr]"
      >
        ${this.renderHeader(reports)}
        ${
					!lookupAnswered.get()
						? html`
              <div
                role="status"
                class="flex flex-col items-center justify-center gap-3 p-6 text-center"
              >
                <schmancy-spinner size="lg" aria-hidden="true"></schmancy-spinner>
                <schmancy-typography type="body" token="sm" class="text-surface-onVariant">
                  ${translate(this.uiLanguage, "scanner.session.connecting")}
                </schmancy-typography>
              </div>
            `
						: reports
							? this.renderSupervisor()
							: this.renderEscalate()
				}
      </div>
    `;
	}

	private renderHeader(reports: boolean) {
		return html`
      <div class="flex items-center gap-2 p-3">
        ${when(
					reports,
					() => html`
            <schmancy-input
              class="min-w-0 flex-1"
              placeholder=${translate(
								this.uiLanguage,
								"scanner.manualLookup.searchPlaceholder",
							)}
              aria-label=${translate(
								this.uiLanguage,
								"scanner.manualLookup.searchPlaceholder",
							)}
              .value=${this.query}
              @input=${(e: CustomEvent<{ value: string }>) => {
								this.query = e.detail.value;
							}}
            ></schmancy-input>
          `,
				)}
        <!-- Don't fucking change that back to before the input this is intentional -->
        <schmancy-icon-button
          data-overlay-close
          icon="close"
          title=${translate(
						this.uiLanguage,
						"scanner.manualLookup.backToScanning",
					)}
          aria-label=${translate(
						this.uiLanguage,
						"scanner.manualLookup.backToScanning",
					)}
          @click=${() => this.close()}
        ></schmancy-icon-button>
      </div>
    `;
	}

	private renderEscalate() {
		return html`
      <div
        class="flex h-full flex-col items-center justify-center gap-3 p-6 text-center"
      >
        <schmancy-icon class="text-4xl text-surface-onVariant"
          >contact_support</schmancy-icon
        >
        <schmancy-typography type="body" token="md">
          ${translate(this.uiLanguage, "scanner.manualLookup.escalateBody", {
						count: answeredTickets.get()?.byTicketId.size ?? 0,
					})}
        </schmancy-typography>
      </div>
    `;
	}

	private renderSupervisor() {
		const trimmed = this.query.trim();
		return html`
      <div class="grid h-full min-h-0 grid-rows-[auto_1fr] gap-3 p-3">
        ${this.renderTypeChips()}
        ${
					trimmed.length === 0
						? guard(
								[
									// The SIZES of the two indexes, never the indexes
									// themselves: `ticketsIndex` is re-set on every scan at
									// every door of this event, and browse renders nothing a
									// scan can change — a row carries a name, an email, a
									// ticket COUNT and a purchase date, no status — so keying
									// on the cell would re-sort every guest by German
									// collation and re-diff every row of the list, on a phone,
									// once per scan. A guest or a ticket ARRIVING moves the
									// count, and that moves the size.
									answeredOrders.get()?.byOrderId.size ?? 0,
									answeredTickets.get()?.byTicketId.size ?? 0,
									originFilter.value,
									this.selectedTypeIds,
									this.uiLanguage,
									// The A—Z rail's own highlight and its scrub bubble live
									// inside this subtree, so a drag has to reach it.
									this.scrubbing,
									this.scrubLetter,
								],
								() => this.renderBrowse(),
							)
						: guard([trimmed, this.selectedTypeIds], () =>
								this.renderResults(trimmed),
							)
				}
      </div>
    `;
	}

	private renderTypeChips() {
		const options = ticketTypeOptions(
			filterByOrigin(lookupOrders.get(), originFilter.value),
			acceptedTypeIds.get(),
		);
		if (options.length < 2) return html``;
		return html`
      <schmancy-scroll direction="horizontal" hide class="w-full min-w-0">
        <schmancy-chips
          multi
          .value=${[...this.selectedTypeIds]}
          @change=${(e: CustomEvent<{ value: string[] }>) => {
						this.selectedTypeIds = new Set(e.detail.value);
					}}
        >
          ${repeat(
						options,
						(id) => id,
						(id) =>
							html`<schmancy-filter-chip value=${id}
                >${ticketTypeName(id, this.uiLanguage) ?? ""}</schmancy-filter-chip
              >`,
					)}
        </schmancy-chips>
      </schmancy-scroll>
    `;
	}

	private renderBrowse() {
		const filtered = filterByTicketTypes(
			filterByOrigin(lookupOrders.get(), originFilter.value),
			this.selectedTypeIds,
		);
		const groups = browseGroups(filtered);
		if (groups.length === 0) {
			return html`
        <div class="flex flex-col gap-1 px-1 pt-2">
          <schmancy-typography type="body" token="md">
            ${translate(this.uiLanguage, "scanner.manualLookup.noTypeMatch")}
          </schmancy-typography>
          <schmancy-typography
            type="body"
            token="sm"
            class="text-surface-onVariant"
          >
            ${translate(this.uiLanguage, "scanner.manualLookup.clearFilterHint")}
          </schmancy-typography>
        </div>
      `;
		}
		return html`
      <div class="grid h-full min-h-0 grid-cols-[1fr_auto] gap-1">
        <div ${overflowWithin()} class="flex flex-col gap-4 pr-1">
          ${repeat(
						groups,
						(g) => g.letter,
						(g) => html`
              <div
                ${ref(this.sectionRef(g.letter))}
                class="flex flex-col gap-1.5"
              >
                <schmancy-list>
                  ${repeat(
										g.orders,
										(o) => o.orderId,
										(o) => this.renderGuestRow(o),
									)}
                </schmancy-list>
              </div>
            `,
					)}
        </div>
        <div
          data-az-rail
          class="touch-none flex h-full w-11 flex-col gap-0.5 rounded-full bg-surface-container/60 px-1 py-1"
          @pointerdown=${(e: PointerEvent) => {
						(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
						this.railPointerDown$.next(e);
					}}
        >
          ${repeat(
						groups,
						(g) => g.letter,
						(g) => {
							const live = this.scrubbing && this.scrubLetter === g.letter;
							return html`
                <button
                  type="button"
                  data-letter=${g.letter}
                  class=${this.classMap({
										"flex flex-1 items-center justify-center rounded-sm font-medium transition-colors duration-100": true,
										"bg-primary-default text-primary-on": live,
										"text-primary-default hover:bg-primary-default hover:text-primary-on focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-default":
											!live,
									})}
                  aria-label=${translate(
										this.uiLanguage,
										"scanner.manualLookup.jumpToLetter",
										{ letter: g.letter },
									)}
                  @click=${() => this.jumpTo(g.letter)}
                >
                  <schmancy-typography type="label" token="sm"
                    >${g.letter}</schmancy-typography
                  >
                </button>
              `;
						},
					)}
        </div>
        ${when(
					this.scrubbing,
					() => html`
            <div
              class="pointer-events-none absolute top-(--bubble-y) left-(--bubble-x) z-20 -translate-x-[calc(100%+0.75rem)] -translate-y-1/2"
            >
              <schmancy-surface
                type="containerHighest"
                opacity="glass"
                elevated
                class="flex h-16 w-16 items-center justify-center rounded-full"
              >
                <schmancy-typography type="display" token="sm"
                  >${this.scrubLetter}</schmancy-typography
                >
              </schmancy-surface>
            </div>
          `,
				)}
      </div>
    `;
	}

	private renderResults(query: string) {
		const filtered = filterByTicketTypes(
			filterByOrigin(lookupOrders.get(), originFilter.value),
			this.selectedTypeIds,
		);
		const matches = rankOrders(query, filtered);
		if (matches.length === 0) {
			return html`
        <div class="flex flex-col gap-1 px-1 pt-2">
          <schmancy-typography type="body" token="md">
            ${translate(this.uiLanguage, "scanner.manualLookup.noMatch")}
          </schmancy-typography>
        </div>
      `;
		}
		return html`
      <div ${overflowWithin()} class="flex flex-col gap-1.5">
        <schmancy-list>
          ${repeat(
						matches,
						(o) => o.orderId,
						(o) => this.renderGuestRow(o),
					)}
        </schmancy-list>
      </div>
    `;
	}

	/** No chevron: `schmancy-list-item` already carries `role="button"` plus
	 *  hover/press physics, so a stacked static glyph on every row earns
	 *  nothing that state doesn't already carry. Recency sits in the
	 *  trailing column under the ticket count, one type-scale step smaller —
	 *  subordinate to the count the way the count is subordinate to the
	 *  name. Every row carries all four facts — name, email, ticket count,
	 *  recency — in both browse and search alike: name and email identify
	 *  the guest, ticket count and recency help confirm the match is
	 *  right. */
	private renderGuestRow(order: LookupOrder) {
		const count = order.tickets.length;
		return html`
      <schmancy-list-item
        @click=${() =>
					// Solid opacity, not the default translucent fill: this stacks
					// over the scanner's live camera feed, and moving video behind a
					// translucent backdrop reads unreadable.
					// showFixEmail/showInvoice/showRefund/showPurge all false — this
					// domain's own order-detail.sync.ts header explains why: the door
					// answers three of that sheet's intents and no more, so an intent it
					// cannot write must not be reachable here.
					show(
						() =>
							html`<order-detail
                .order=${{ ...order, id: order.orderId }}
                .showFixEmail=${false}
                .showInvoice=${false}
                .showRefund=${false}
                .showPurge=${false}
              ></order-detail>`,
						{
							opacity: "solid",
							ariaLabel: translate(
								this.uiLanguage,
								"scanner.manualLookup.guestSheetAriaLabel",
								{
									name: order.buyerName,
								},
							),
						},
					)
						.pipe(takeUntil(this.disconnecting))
						.subscribe()}
      >
        <div class="flex flex-col items-start gap-0.5 py-1.5">
          <schmancy-typography type="body" token="lg"
            >${order.buyerName}</schmancy-typography
          >
          <schmancy-typography
            type="body"
            token="sm"
            class="text-surface-onVariant"
          >
            ${order.buyerEmail}
          </schmancy-typography>
        </div>
        <div slot="trailing" class="flex flex-col items-end gap-0.5 py-1.5">
          <schmancy-typography
            type="label"
            token="sm"
            class="text-surface-onVariant"
          >
            ${count}
            ${translate(
							this.uiLanguage,
							count === 1
								? "scanner.manualLookup.ticketCountSingular"
								: "scanner.manualLookup.ticketCountPlural",
						)}
          </schmancy-typography>
          <schmancy-typography
            type="label"
            token="xs"
            class="text-surface-onVariant"
          >
            ${purchaseAgo(order.authorisedAt.toDate(), this.uiLanguage)}
          </schmancy-typography>
        </div>
      </schmancy-list-item>
    `;
	}

	private sectionRef(letter: string): Ref<HTMLDivElement> {
		const existing = this.sectionRefs.get(letter);
		if (existing) return existing;
		const created = createRef<HTMLDivElement>();
		this.sectionRefs.set(letter, created);
		return created;
	}

	private jumpTo(letter: string) {
		this.sectionRef(letter).value?.scrollIntoView({
			block: "start",
			behavior: "instant",
		});
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"manual-lookup-view": ManualLookupView;
	}
}
